import { t } from "i18next";
import { FC, useEffect, useMemo, useState } from "react";
import { Control, useController } from "react-hook-form";

// Utilities
import { getDecimalPlaces } from "@/_helpers/number_functions";

// Styles
import { Add, Subtract } from '@carbon/icons-react';

// Components
import IconButton from "./icon_button";
import { TextField, Box, TextFieldProps, Skeleton } from "@mui/material";


interface InteratorInputProps {
  name: string;
  control: Control<any>;
  //REVIEW will this work?
  size?: 'small' | 'medium' | 'large';
  label?: string;
  valueText?: string;
  iterationValue?: number;
  helperText?: string;
  tabIndex?: number;
  maxValue?: number;
  gutter?: string;
  allowZero?: boolean;
  hideErrorMessage?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onChange?: (value: number) => void;
  sx?: TextFieldProps['sx'];
}

const IteratorInput: FC<InteratorInputProps>  = ({
  name,
  control,
  size = 'small',
  label,
  valueText,
  iterationValue = 1,
  maxValue = 10,
  helperText,
  tabIndex = -1,
  gutter = '12px',
  allowZero = true,
  hideErrorMessage = false,
  disabled = false,
  isLoading = false,
  onChange,
  sx
}) => {

  const { field, fieldState } = useController({name, control});
  const setValueText = valueText ? t(valueText, {count: Number(field.value ?? 0)}) : field.value?.toString() ?? '';
  const [inputValue, setInputValue] = useState<string>(setValueText);

  const decimalPlaces = useMemo(() => {
    return getDecimalPlaces(iterationValue);
  }, [iterationValue]);

  const skeletonClasses = {
    'Skeleton__field-small': size == 'small',
    'Skeleton__field-medium': size == 'medium',
    'Skeleton__field-large': size == 'large',
  }
  
  const classes = {
    'TextInput': true,
    'TextInput-small': size == 'small',
    'TextInput-medium': size == 'medium',
    'TextInput-large': size == 'large',
  };

  const styles = {
    ...sx,
    '& .Mui-disabled': {
      '-webkitTextFillColor': disabled ? 'unset' : 'var(--text-primary) !important',
    },
    '& .MuiInputBase-root': {
      paddingRight: '4px !important',
    }
  }

  const validSkeletonClasses = Object.entries(skeletonClasses)
    .filter(([,v]) => !!v)
    .map(([k,]) => k)
    .join(' ')

  const validClasses = Object.entries(classes)
      .filter(([,v]) => !!v)
      .map(([k,]) => k)
      .join(' ')

  useEffect(() => {
    if (field.value !== inputValue) setInputValue(setValueText);
  }, [field.value]);

  const handleDecreaseServing = () => {
    if (disabled || !inputValue) return;
    if (!allowZero && parseFloat(inputValue) - iterationValue <= 0) return;
    const newValue = (parseFloat(inputValue) - iterationValue).toFixed(decimalPlaces);
    field.onChange(parseFloat(newValue));
    onChange && onChange(parseFloat(newValue));
  }

  const handleIncreaseServing = () => {
    if (disabled) return;
    if (maxValue && parseFloat(inputValue) + iterationValue > maxValue) return;
    const newValue = (parseFloat(inputValue) + iterationValue).toFixed(decimalPlaces);
    field.onChange(parseFloat(newValue));
    onChange && onChange(parseFloat(newValue));
  }
  
  const handleBlur = () => {
    if (inputValue.endsWith(".")) {
      const newValue = inputValue.slice(0, -1);
      setInputValue(newValue);
      field.onChange(newValue === "" ? '' : parseFloat(newValue));
    } else if (decimalPlaces !== undefined && decimalPlaces > 0) {
      field.onChange(inputValue === "" ? '' : parseFloat(inputValue));
    } else {
      field.onChange(inputValue === "" ? '' : parseInt(inputValue));
    }
  } 

  return (
    // Width set to 100% to always fill what ever container it is in
    <Box className='input__container' sx={{display: 'flex', flexDirection: 'column', alignItems: 'start', marginBottom: gutter, width: '100%'}}>
      {label && (isLoading ? <Skeleton variant="text" height="16px" width="100" style={{marginBottom: '6px'}} /> : 
        <Box display="flex" width="100%">
          <span className="label-text-02" style={{marginBottom: '0.5em', color: 'var(--text-secondary)'}}>{label}</span>
        </Box>)}

      {isLoading ? <Skeleton className={validSkeletonClasses} variant="rounded" width="100%" /> :
        <TextField
          className={validClasses}
          value={inputValue || ''}
          inputProps={{
            ref: field.ref,
            name: field.name,
          }}
          tabIndex={tabIndex}
          error={fieldState.invalid}
          helperText={helperText && !fieldState.error?.message ? helperText : hideErrorMessage ? null : fieldState.error?.message}
          disabled={true}
          onBlur={handleBlur}
          sx={styles}
          InputProps={{
            endAdornment: (
              <>
                <IconButton
                  kind="ghost"
                  size={size}
                  sx={{padding: '0px', marginRight: '4px'}}
                  icon={<Subtract size="16px" />}
                  disabled={disabled}
                  onClick={handleDecreaseServing}
                  />
                <Box height="20px" border="0.5px solid var(--border-subtle-01)" sx={{color: 'var(--border-subtle-01)'}} />
                <IconButton
                  kind="ghost"
                  size={size}
                  sx={{padding: '0px', marginLeft: '4px'}}
                  icon={<Add size="16px" />}
                  disabled={disabled}
                  onClick={handleIncreaseServing}
                  />
              </>
            ),
          }}
        />}
    </Box>
  );
};

  export default IteratorInput;